// src/components/Sidebar/Sidebar.jsx
import React, { useEffect, useState, useMemo, useCallback } from "react";
import "./Sidebar.css";
import { useTranslation } from "react-i18next";
import SidebarItem from "./SidebarItem";
import rawItems from "../../data/sidebar.json";
import useSubscriptionFeatureStore from "../../store/useSubscriptionFeatureStore";
import useFeatureStore from "../../store/useFeatureStore";
import useBatchTrackingStore from "../../store/useBatchTrackingStore";
import { useLocation } from "react-router-dom";
import { isItemActiveRecursive } from "../../utils/sidebarUtils";

/** Recursively strips items the user's subscription or feature flags doesn't include */
function filterByFeature(items, featureCodes, isBatchTrackingEnabled) {
  return items.reduce((acc, item) => {
    // Drop this item entirely if required feature is missing
    if (item.requiredFeature && !featureCodes.includes(item.requiredFeature)) {
      return acc;
    }
    // Drop this item entirely if required feature flag is missing
    if (item.requiredFeatureFlag && !useFeatureStore.getState().isFeatureEnabled(item.requiredFeatureFlag)) {
      return acc;
    }
    // Drop this item entirely if batch tracking is required but not enabled
    if (item.requiredBatchTracking && !isBatchTrackingEnabled) {
      return acc;
    }
    // Recurse into children
    if (item.childrens?.length) {
      const filteredChildren = filterByFeature(item.childrens, featureCodes, isBatchTrackingEnabled);
      // Drop the parent if all its children were filtered out
      if (filteredChildren.length === 0) return acc;
      return [...acc, { ...item, childrens: filteredChildren }];
    }
    return [...acc, item];
  }, []);
}


const Sidebar = () => {
  const location = useLocation();
  const { t } = useTranslation();
  const [isMinimized, setIsMinimized] = useState(false);
  const [openItemId, setOpenItemId] = useState(null);

  const featureCodes = useSubscriptionFeatureStore((state) => state.featureCodes);
  const featureCodesLoaded = useSubscriptionFeatureStore((state) => state.featureCodesLoaded);
  const features = useFeatureStore((state) => state.features);
  const isBatchTrackingEnabled = useBatchTrackingStore((state) => state.isBatchTrackingEnabled);

  // Re-filter only when featureCodes, features, or isBatchTrackingEnabled changes
  const items = useMemo(
    () => filterByFeature(rawItems, featureCodes, isBatchTrackingEnabled),
    [featureCodes, features, isBatchTrackingEnabled]
  );

  // Auto-expand based on path change
  useEffect(() => {
    const activeItem = items.find((item) =>
      isItemActiveRecursive(item, location.pathname)
    );
    if (activeItem) {
      setOpenItemId(activeItem.title);
    }
  }, [location.pathname, items]);

  const toggleSidebar = useCallback(() => setIsMinimized((p) => !p), []);

  // Memoize the sidebar class name
  const sidebarClassName = useMemo(
    () => `sidebar ${isMinimized ? "minimized" : ""}`,
    [isMinimized]
  );

  // Memoize the minimize button transform
  const minimizeBtnClassName = useMemo(
    () => `minimize-btn ${isMinimized ? "rotated" : ""}`,
    [isMinimized]
  );



  useEffect(() => {
    ["/assets/black-dot.png", "/assets/dot-icon.png", "/assets/Expand.png"]
      .forEach((src) => { const img = new Image(); img.src = src; });
  }, []);

  // Don't render items until we know which features are allowed
  // (avoids a flash of restricted items)
  if (!featureCodesLoaded) return <div className="sidebar" />;

  return (
    <div className={sidebarClassName}>
      <div className="sidebar-options">
        {items.map((item, index) => (
          <SidebarItem
            key={item.title || index}
            item={item}
            isMinimized={isMinimized}
            isOpen={openItemId === item.title}
            onToggle={() =>
              setOpenItemId(openItemId === item.title ? null : item.title)
            }
          />
        ))}
      </div>
      <div
        className="minimize cr-pointer"
        onClick={toggleSidebar}
        data-tooltip-id="global-tooltip"
        data-tooltip-content={isMinimized ? t("Expand Sidebar") : t("Collapse Sidebar")}
      >
        <svg
          className={minimizeBtnClassName}
          viewBox="0 0 12 23"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M3.05388 11.5L12 21.0836L10.211 23L0.370429 12.4582C0.133243 12.2041 0 11.8594 0 11.5C0 11.1406 0.133243 10.7959 0.370429 10.5418L10.211 0L12 1.91644L3.05388 11.5Z"
            fill="#424242"
          />
        </svg>
      </div>
    </div>
  );
};

export default Sidebar;
